import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { format } from "date-fns";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { FilterItem } from "@sscs/types";
import {
  Button,
  Dialog,
  Form,
  KeyValueList,
  Toast,
  multistepFilterOperators,
} from "@sscs/ui";
import { DATE_FIELD_FORMAT, TIME_FIELD_FORMAT } from "@sscs/utils";
import { apiErrorToast } from "Utils";
import { AUDIT_FILTER_OVERVIEW_LABELS } from "../../constants";
import { useExportAuditLogsMutation } from "../../services/audit.api";
import { AuditFilters, ExportFormData } from "../../types";
import { AuditExportSchema } from "../../utils";
import styles from "./exportDialog.module.scss";

type ExportDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  datetimeStart: Date;
  datetimeEnd: Date;
  filters: AuditFilters;
};

const ExportDialogContent = ({
  onClose,
  datetimeStart,
  datetimeEnd,
  filters,
}: Omit<ExportDialogProps, "isOpen">): React.JSX.Element => {
  const formMethods = useForm<ExportFormData>({
    resolver: yupResolver(AuditExportSchema),
    values: {
      name: "",
      should_create_cef: false,
      should_create_ndjson: false,
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    trigger,
  } = formMethods;

  const [exportAuditLogs] = useExportAuditLogsMutation();

  const datetimeFormat = `${DATE_FIELD_FORMAT} ${TIME_FIELD_FORMAT}`;

  const onSubmit: SubmitHandler<ExportFormData> = async (data) => {
    try {
      await exportAuditLogs({
        body: {
          ...data,
          filters: filters,
          datetime_start: datetimeStart.toISOString(),
          datetime_end: datetimeEnd.toISOString(),
        },
      }).unwrap();

      Toast.show({
        title: "Audit logs exported successfully",
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not export audit logs");
    }

    onClose();
  };

  const appliedFilters = [
    {
      "Time range": `${format(datetimeStart, datetimeFormat)} to ${format(datetimeEnd, datetimeFormat)}`,
      ...Object.fromEntries(
        Object.entries(filters).map(
          ([filterName, filter]: [keyof AuditFilters, FilterItem]) => {
            return [
              AUDIT_FILTER_OVERVIEW_LABELS[filterName],
              <span key={filterName}>
                {multistepFilterOperators[filter.operation]}{" "}
                {filter.values.length === 1
                  ? filter.values[0]
                  : filter.values.join(", ")}
              </span>,
            ];
          },
        ),
      ),
    },
  ];

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>Export audit logs</Dialog.Header.Title>
      </Dialog.Header>
      <FormProvider {...formMethods}>
        <Form.Form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Title>Configure</Dialog.Section.Title>
              <Dialog.Section.Description>
                Download links expire after 7 days.
              </Dialog.Section.Description>
              <Dialog.Section.Content>
                <Form.TextField
                  name="name"
                  label="Name"
                  placeholder="e.g. Q1 security audit"
                  errors={errors}
                  isRequired
                />
                <div className={styles["export-type-field"]}>
                  <div className={styles["export-type-label"]}>
                    Select export type
                  </div>
                  <div className={styles["export-type-container"]}>
                    <Form.Checkbox
                      name="should_create_ndjson"
                      label="NDJSON"
                      errors={errors}
                    />
                    <Form.Checkbox
                      name="should_create_cef"
                      label="CEF"
                      onChange={() => trigger("should_create_ndjson")}
                    />
                  </div>
                </div>
              </Dialog.Section.Content>
            </Dialog.Section>
            <KeyValueList list={["Applied filters", ...appliedFilters]} />
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" color="primary">
                Export
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const ExportDialog = ({
  isOpen,
  ...props
}: ExportDialogProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <ExportDialogContent {...props} />
  </Dialog.Frame>
);

export default ExportDialog;
